/**
 * A tag scanner for MJML and the HTML inside it — Block 0.4 of the marketing
 * plan (plans/execution-plan-2026-09.md §2.2).
 *
 * There is no DOM and no `DOMParser` on workerd, and the browser build of
 * MJML validates structure but passes the content of `mj-text` through
 * unescaped, so the allowlists in `./index.ts` need their own view of the
 * source. This is deliberately a scanner, not a parser: it yields tags,
 * text and comments with line numbers, and leaves every judgement to the
 * caller. It never throws — a malformed tag is a token that says so.
 */

export interface TagToken {
  kind: 'open' | 'close' | 'self';
  /** Lower-cased. */
  name: string;
  /** Lower-cased names; values verbatim, quotes stripped. */
  attrs: Record<string, string>;
  line: number;
}

export interface TextToken {
  kind: 'text';
  text: string;
  line: number;
}

export interface CommentToken {
  kind: 'comment';
  line: number;
}

export interface BadToken {
  kind: 'bad';
  message: string;
  line: number;
}

export type Token = TagToken | TextToken | CommentToken | BadToken;

const NAME = /[A-Za-z][A-Za-z0-9:-]*/y;
const SPACE = /\s*/y;
const ATTR_NAME = /[^\s=/>"']+/y;
const UNQUOTED = /[^\s>]+/y;

function skip(pattern: RegExp, src: string, at: number): number {
  pattern.lastIndex = at;
  pattern.exec(src);
  return pattern.lastIndex;
}

function countLines(src: string, from: number, to: number): number {
  let n = 0;
  for (let k = from; k < to; k++) if (src.charCodeAt(k) === 10) n++;
  return n;
}

export function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  let line = 1;

  while (i < src.length) {
    const lt = src.indexOf('<', i);
    if (lt === -1) {
      out.push({ kind: 'text', text: src.slice(i), line });
      break;
    }
    if (lt > i) {
      out.push({ kind: 'text', text: src.slice(i, lt), line });
      line += countLines(src, i, lt);
    }

    if (src.startsWith('<!--', lt)) {
      const end = src.indexOf('-->', lt + 4);
      if (end === -1) {
        out.push({ kind: 'bad', message: 'Unterminated comment.', line });
        break;
      }
      out.push({ kind: 'comment', line });
      line += countLines(src, lt, end + 3);
      i = end + 3;
      continue;
    }

    if (src.startsWith('</', lt)) {
      NAME.lastIndex = lt + 2;
      const name = NAME.exec(src);
      const gt = src.indexOf('>', lt);
      if (!name || gt === -1 || skip(SPACE, src, NAME.lastIndex) !== gt) {
        out.push({ kind: 'bad', message: 'Malformed closing tag.', line });
        if (gt === -1) break;
        line += countLines(src, lt, gt + 1);
        i = gt + 1;
        continue;
      }
      out.push({ kind: 'close', name: name[0].toLowerCase(), attrs: {}, line });
      line += countLines(src, lt, gt + 1);
      i = gt + 1;
      continue;
    }

    NAME.lastIndex = lt + 1;
    const name = NAME.exec(src);
    if (!name) {
      // `<` followed by a space, a digit, `!`, `?` — not a tag. HTML would
      // treat it as text; an email author meant `&lt;`, or made a mistake.
      out.push({
        kind: 'bad',
        message: 'A "<" that does not begin a tag must be written as &lt;.',
        line,
      });
      i = lt + 1;
      continue;
    }

    const attrs: Record<string, string> = {};
    let j = NAME.lastIndex;
    let self = false;
    let closed = false;
    while (j < src.length) {
      j = skip(SPACE, src, j);
      if (src.startsWith('/>', j)) {
        self = true;
        j += 2;
        closed = true;
        break;
      }
      if (src[j] === '>') {
        j += 1;
        closed = true;
        break;
      }
      ATTR_NAME.lastIndex = j;
      const attr = ATTR_NAME.exec(src);
      if (!attr) break;
      j = ATTR_NAME.lastIndex;
      j = skip(SPACE, src, j);
      let value = '';
      if (src[j] === '=') {
        j = skip(SPACE, src, j + 1);
        const quote = src[j];
        if (quote === '"' || quote === "'") {
          const end = src.indexOf(quote, j + 1);
          if (end === -1) break;
          value = src.slice(j + 1, end);
          j = end + 1;
        } else {
          UNQUOTED.lastIndex = j;
          const unquoted = UNQUOTED.exec(src);
          if (!unquoted) break;
          value = unquoted[0];
          j = UNQUOTED.lastIndex;
        }
      }
      attrs[attr[0].toLowerCase()] = value;
    }

    if (!closed) {
      // Everything after a malformed tag is guesswork; say so once and stop.
      out.push({ kind: 'bad', message: `Malformed tag <${name[0]}>.`, line });
      break;
    }

    out.push({ kind: self ? 'self' : 'open', name: name[0].toLowerCase(), attrs, line });
    line += countLines(src, lt, j);
    i = j;
  }

  return out;
}
